import { z } from 'zod';
import { ConditionOperatorEnum, ConditionSchema, conditionGroupSchema, segmentConditionGroupsSchema, } from './conditions.js';
import { ReachFeature } from './features.js';
import { JSONSchemaType } from './schema-mappings.js';
import { SegmentDefinitionTypeEnum } from './segment-definitions.js';
/**
 * Request body for creating a business segment
 * @see POST /api/business-segment/
 *
 * Supports two formats:
 * 1. Legacy format: Use 'conditions' array for simple field conditions
 * 2. New format: Use 'conditionGroups' for complex conditions with related resources and AND/OR logic
 *
 * If conditionGroups is provided, it takes precedence over conditions.
 */
export const createBusinessSegmentRequestBody = z.object({
    name: z.string().describe('A friendly name for the segment'),
    description: z.string().describe('A friendly description for the segment').optional(),
    conditions: z
        .array(ConditionSchema)
        .describe('The conditions to include in the segment (legacy format)')
        .optional(),
    conditionGroups: z
        .array(conditionGroupSchema)
        .describe('Condition groups with AND/OR logic and related resource support (new format)')
        .optional(),
    type: z
        .enum([SegmentDefinitionTypeEnum.CUSTOM, SegmentDefinitionTypeEnum.ONE_OFF])
        .describe('The type of segment')
        .default(SegmentDefinitionTypeEnum.CUSTOM)
        .optional(),
});
/**
 * Response body for creating a business segment
 * @see POST /api/business-segment/
 */
export const createBusinessSegmentResponseBody = z.object({
    id: z.string().describe('The id of the segment'),
    name: z.string().describe('A friendly name for the segment'),
    description: z.string().describe('A friendly description for the segment').optional(),
    createdAt: z.date().nullable().describe('The date and time the segment was created'),
    type: z.nativeEnum(SegmentDefinitionTypeEnum).describe('The type of segment'),
});
/**
 * Request body for updating a business segment
 * @see PATCH /api/business-segment/{segmentId}
 */
export const updateBusinessSegmentRequestBody = z.object({
    name: z.string().optional().describe('A friendly name for the segment'),
    description: z.string().optional().describe('A friendly description for the segment'),
    conditions: z
        .array(ConditionSchema)
        .optional()
        .describe('The conditions to include in the segment (legacy format)'),
    conditionGroups: z
        .array(conditionGroupSchema)
        .optional()
        .describe('Condition groups with AND/OR logic and related resource support (new format)'),
});
/**
 * Response body for updating a business segment
 * @see PATCH /api/business-segment/{segmentId}
 */
export const updateBusinessSegmentResponseBody = z.object({
    id: z.string().describe('The id of the segment'),
    name: z.string().describe('A friendly name for the segment'),
    description: z.string().describe('A friendly description for the segment').optional(),
    createdAt: z.date().nullable().describe('The date and time the segment was created'),
    type: z.nativeEnum(SegmentDefinitionTypeEnum).describe('The type of segment'),
});
/**
 * Response body for listing business segments
 * @see GET /api/business-segment/
 */
export const listBusinessSegmentsResponseBody = z.object({
    results: z
        .array(z.object({
        id: z.string().describe('The id of the segment'),
        name: z.string().describe('A friendly name for the segment'),
        description: z.string().describe('A friendly description for the segment').optional(),
        createdAt: z.date().nullable().describe('The date and time the segment was created'),
        type: z.nativeEnum(SegmentDefinitionTypeEnum).describe('The type of segment'),
        userCount: z.number().describe('The number of users in the segment'),
        phoneCount: z.number().describe('The number of phone numbers in the segment'),
        emailCount: z.number().describe('The number of emails in the segment'),
        userCountUpdatedAt: z.date().describe('The date and time the user count was updated'),
    }))
        .describe('The segments'),
    pagination: z.object({
        hasNextPage: z.boolean().nullable().describe('Whether there is a next page'),
        cursor: z.string().nullable().describe('The cursor to start from'),
        total: z.number().nullable().describe('The total number of segments'),
    }),
});
/**
 * Response body for getting a single business segment
 * @see GET /api/business-segment/{segmentId}
 */
export const getBusinessSegmentResponseBody = z.object({
    id: z.string().describe('The id of the segment'),
    name: z.string().describe('A friendly name for the segment'),
    description: z.string().describe('A friendly description for the segment').optional(),
    createdAt: z.date().nullable().describe('The date and time the segment was created'),
    type: z.nativeEnum(SegmentDefinitionTypeEnum).describe('The type of segment'),
    conditions: z
        .array(ConditionSchema)
        .describe('The conditions to include in the segment (legacy format)'),
    conditionGroups: z
        .array(conditionGroupSchema)
        .optional()
        .describe('Condition groups with AND/OR logic and related resource support (new format)'),
});
/**
 * Response body for getting available segment conditions
 * @see GET /api/business-segment/conditions
 */
export const getBusinessSegmentConditionsResponseBody = z.object({
    conditions: z
        .array(z.object({
        field: z.string().describe('The field to include in the segment'),
        operators: z.array(ConditionOperatorEnum).describe('The operators to include in the segment'),
        type: z.nativeEnum(JSONSchemaType).describe('The type of segment'),
        itemType: z
            .nativeEnum(JSONSchemaType)
            .optional()
            .describe('If the type is an array, this is the type of the items'),
        options: z
            .array(z.object({
            displayName: z.string().describe('The display name of the option'),
            id: z.string().describe('The id of the option'),
        }))
            .optional()
            .describe('The options to include in the segment'),
        description: z.string().optional().describe('The description of the condition'),
    }))
        .describe('The conditions to include in the segment'),
});
/**
 * Response body for getting count of recipients in segments
 * @see GET /api/business-segment/count
 */
export const getBusinessSegmentCountResponseBody = z.object({
    count: z.number().describe('The number of users in the segment'),
    recipients: z
        .array(z.object({
        firstName: z.string().nullable().describe('The first name of the recipient'),
        lastName: z.string().nullable().describe('The last name of the recipient'),
        email: z.string().nullable().describe('The email of the recipient'),
        phone: z.string().nullable().describe('The phone of the recipient'),
        id: z.string().nullable().describe('The reach id of the recipient'),
        externalId: z.string().nullable().describe('The external id of the recipient'),
        emailOptedOut: z.boolean().optional().describe('Whether the user has opted out of email'),
        smsOptedOut: z.boolean().optional().describe('Whether the user has opted out of SMS'),
    }))
        .describe('The recipients in the segment (paginated)'),
    pagination: z
        .object({
        hasNextPage: z.boolean().describe('Whether there are more pages'),
        cursor: z.string().nullable().describe('Cursor for the next page (null if no more pages)'),
    })
        .describe('Pagination metadata'),
});
/**
 * Response body for text-to-segment conversion
 * @see POST /api/business-segment/text-to-segment
 */
export const textToBusinessSegmentResponseBody = z.object({
    success: z.boolean().describe('Whether the text to segment was successful'),
    failureReason: z.string().optional().describe('The reason the text to segment was not successful.'),
    conditionGroups: segmentConditionGroupsSchema
        .optional()
        .describe('The condition groups to include in the segment. Groups are AND-joined with each other; conditions within a group use that group logic.'),
    title: z.string().optional().describe('A suggested title for the segment generated by AI'),
    description: z.string().optional().describe('A suggested description for the segment generated by AI'),
});
/** Upper bound on the plain-language description a partner can submit (abuse/cost control). */
export const PARTNER_TEXT_TO_SEGMENT_MAX_LENGTH = 2000;
/**
 * Request body for the partner text-to-segment endpoint. Unlike the tenant flow,
 * there is no `userCustomVerb`; the platform is derived from auth context.
 * @see POST /partner/segments/text-to-segment
 */
export const textToPartnerSegmentRequestBody = z.object({
    text: z
        .string()
        .min(1)
        .max(PARTNER_TEXT_TO_SEGMENT_MAX_LENGTH)
        .describe('Plain-language description of the segment to build'),
});
/**
 * Response for enqueueing a partner segment generation. Generation runs in
 * processor-segments; poll the status endpoint with this job id.
 * @see POST /partner/segments/text-to-segment
 */
export const partnerSegmentGenerationEnqueuedResponse = z.object({
    jobId: z.string().uuid().describe('Job id to poll for the generated segment'),
});
export var PartnerSegmentGenerationStatus;
(function (PartnerSegmentGenerationStatus) {
    PartnerSegmentGenerationStatus["PROCESSING"] = "processing";
    PartnerSegmentGenerationStatus["COMPLETED"] = "completed";
    PartnerSegmentGenerationStatus["FAILED"] = "failed";
})(PartnerSegmentGenerationStatus || (PartnerSegmentGenerationStatus = {}));
export const partnerSegmentGenerationPathParams = z.object({
    jobId: z.string().uuid().describe('Job id returned when the generation was enqueued'),
});
/**
 * Poll response for a partner segment generation job. `result` is present once
 * the job completes; a completed generation can still carry `success: false`
 * (e.g. the description was too vague to compile).
 * @see GET /partner/segments/text-to-segment/:jobId
 */
export const partnerSegmentGenerationStatusResponse = z.object({
    status: z.nativeEnum(PartnerSegmentGenerationStatus),
    result: textToBusinessSegmentResponseBody.optional(),
});
/**
 * @see @business-segment.routes.ts - /list
 */
export const listBusinessSegmentsRequestQueryParams = z.object({
    cursor: z.string().optional().describe('The cursor to start from'),
    limit: z.coerce.number().optional().describe('The limit of items to return, default is 100'),
    search: z.string().optional().describe('The search query to filter by segment name'),
    feature: z
        .nativeEnum(ReachFeature)
        .optional()
        .describe('Filter segments to those scoped to this product feature or unscoped segments'),
});
//# sourceMappingURL=business-segments.js.map